/**
 * Resumen mensual OFICIAL del cliente — descarga del PDF y del Excel (round 2026-10-29).
 *
 *   GET /api/finanzas/resumen-mensual/:mes/:formato
 *   GET /api/finanzas/resumen-mensual/:mes/:formato/propiedad/:propId      (+ ?clienteId= solo administrador)
 *
 * El documento lo arma el SERVIDOR al vuelo (compuerta de publicación incluida): este módulo solo pide el binario
 * con la sesión por cookie y lo entrega al navegador. Por qué no `apiFetch`: siempre hace `res.json()`, y aquí el
 * cuerpo es un archivo. Por qué no `descargarVerificado` (utils/descargaArchivo.ts): comprueba con un HEAD, y Express
 * ejecuta el handler del GET también para un HEAD, así que cada descarga generaría el documento DOS veces.
 *
 * Qué hace:
 *   - `fetch` a `API_BASE_URL` con la cookie (el interceptor global de `apiClient` ya agrega `credentials` y convierte
 *     un 401 en cierre de sesión, que es lo correcto: la sesión cayó).
 *   - Si el servidor no entrega un archivo, lee el JSON de error y lo traduce a un texto en español para la persona
 *     (el 409 dice que el mes aún no está publicado; el 429, que espere). Nunca guarda un JSON de error como si fuera
 *     un PDF: además del `ok`, comprueba el `Content-Type`.
 *   - El nombre del archivo es el que fija el servidor (`Content-Disposition`, que el backend expone por CORS); si el
 *     navegador no lo deja leer, lo arma igual que el servidor.
 *   - Dispara la descarga con un ancla sobre una URL `blob:` (mismo origen, así que el nombre SÍ se respeta) y la revoca.
 */
import { API_BASE_URL } from "./apiClient";

export type FormatoResumen = "pdf" | "xlsx";

export const MIME_RESUMEN: Record<FormatoResumen, string> = {
  pdf: "application/pdf",
  xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
};

export interface DescargaResumenParams {
  /** Mes en formato `YYYY-MM` (el servidor valida el rango 01–12). */
  mes: string;
  formato: FormatoResumen;
  /** Solo esta propiedad. Ausente o `null` = todas las del cliente. */
  propId?: number | null;
  /** Solo administrador (obligatorio para él): de qué cliente es el documento. Un cliente no lo envía. */
  clienteId?: number | null;
  /** Nombre de la propiedad, solo para armar el nombre del archivo si el navegador no puede leer el del servidor. */
  nombrePropiedad?: string | null;
}

/** Fallo de la descarga con un texto ya listo para mostrar. */
export class ErrorDescargaResumen extends Error {
  /** HTTP del servidor; `0` si no hubo respuesta (sin red). */
  readonly status: number;
  /** Código del error del backend (`MES_NO_PUBLICADO`, `RATE_LIMITED`, …), si lo hubo. */
  readonly code?: string;

  constructor(message: string, status: number, code?: string) {
    super(message);
    this.name = "ErrorDescargaResumen";
    this.status = status;
    this.code = code;
  }
}

/** Texto de cada error conocido del backend; lo que no esté aquí usa el del servidor o el genérico del estado. */
export const MENSAJES_RESUMEN = {
  mesNoPublicado:
    "Este mes aún no está publicado. Cuando Aloja Fácil cierre el mes podrás descargar el resumen.",
  sinDatos: "No se encontró información para generar este resumen.",
  limite: "Pediste varios resúmenes seguidos. Espera un minuto antes de descargar otro.",
  sinPermiso: "No tienes permiso para descargar este resumen.",
  sesion: "Tu sesión expiró. Vuelve a iniciar sesión.",
  servidor: "No se pudo generar el documento. Inténtalo de nuevo en unos segundos.",
  red: "No se pudo conectar con el servidor. Revisa tu conexión e inténtalo de nuevo.",
  invalido: "El servidor no entregó un documento válido. Inténtalo de nuevo.",
  archivo: "No se pudo guardar el archivo en tu dispositivo. Inténtalo de nuevo.",
  parametros: "Los datos de la descarga no son válidos. Revisa el mes y la propiedad.",
} as const;

/** Ruta (sin `API_BASE_URL`) del documento. Los parámetros viajan en el PATH: así la bitácora del servidor los registra. */
export function rutaResumenMensual({
  mes,
  formato,
  propId,
  clienteId,
}: Pick<DescargaResumenParams, "mes" | "formato" | "propId" | "clienteId">): string {
  const base = `/finanzas/resumen-mensual/${encodeURIComponent(mes)}/${formato}`;
  const alcance = propId != null ? `/propiedad/${propId}` : "";
  const query = clienteId != null ? `?clienteId=${clienteId}` : "";
  return `${base}${alcance}${query}`;
}

/** Quita lo que un sistema de archivos no admite; el servidor ya manda su nombre saneado, esto es solo cinturón. */
function sanearNombre(nombre: string): string {
  // Los controles (saltos de línea, tabuladores…) se descartan con un filtro y no con una regex `\u0000-\u001f`
  // (la regla `no-control-regex` de ESLint la marca, y en CI un warning de CRA rompe el build).
  const sinControles = Array.from(nombre)
    .filter((c) => c.charCodeAt(0) >= 32)
    .join("");
  return sinControles
    .replace(/[\\/:;*?"<>|]/g, "_")
    .replace(/\s+/g, " ")
    .trim();
}

/**
 * Nombre del archivo igual al que arma el servidor: `AlojaFacil_ResumenMensual_2026-10[_<PROPIEDAD>].pdf|xlsx`.
 * Solo se usa si el navegador no deja leer `Content-Disposition`.
 */
export function nombreArchivoResumen({
  mes,
  formato,
  propId,
  nombrePropiedad,
}: Pick<DescargaResumenParams, "mes" | "formato" | "propId" | "nombrePropiedad">): string {
  const sufijo =
    propId != null && nombrePropiedad
      ? `_${sanearNombre(nombrePropiedad).toUpperCase().replace(/\s+/g, "_")}`
      : "";
  return `AlojaFacil_ResumenMensual_${mes}${sufijo}.${formato}`;
}

/**
 * Nombre que propone el servidor en `Content-Disposition` (`filename*=UTF-8''…` primero, luego `filename="…"`), o
 * `null` si no viene o no se puede leer.
 */
export function nombreDesdeContentDisposition(cabecera: string | null | undefined): string | null {
  if (!cabecera) return null;
  const extendido = /filename\*\s*=\s*UTF-8''([^;]+)/i.exec(cabecera);
  if (extendido) {
    try {
      const nombre = sanearNombre(decodeURIComponent(extendido[1].trim()));
      if (nombre) return nombre;
    } catch {
      /* percent-encoding roto: se prueba con el nombre simple */
    }
  }
  const simple = /filename\s*=\s*"([^"]+)"/i.exec(cabecera) ?? /filename\s*=\s*([^;]+)/i.exec(cabecera);
  const nombre = simple ? sanearNombre(simple[1].trim()) : "";
  return nombre || null;
}

/** Saca el `{ code, message }` de cualquiera de los formatos de error del backend. */
function leerError(data: unknown): { code?: string; message?: string } {
  const d = data as { message?: unknown; error?: unknown } | null | undefined;
  if (!d || typeof d !== "object") return {};
  const e = d.error;
  if (e && typeof e === "object") {
    const { code, message } = e as { code?: unknown; message?: unknown };
    return {
      code: typeof code === "string" ? code : undefined,
      message: typeof message === "string" ? message : undefined,
    };
  }
  if (typeof e === "string") return { message: e };
  return { message: typeof d.message === "string" ? d.message : undefined };
}

/** Texto para la persona según lo que contestó el servidor. */
export function mensajeParaError(status: number, data?: unknown): string {
  const { code, message } = leerError(data);
  if (code === "MES_NO_PUBLICADO" || status === 409) return MENSAJES_RESUMEN.mesNoPublicado;
  if (code === "RATE_LIMITED" || status === 429) return MENSAJES_RESUMEN.limite;
  if (status === 401) return MENSAJES_RESUMEN.sesion;
  if (status === 403) return MENSAJES_RESUMEN.sinPermiso;
  if (status === 404) return MENSAJES_RESUMEN.sinDatos;
  // El 413 (documento demasiado extenso) y el 400 del administrador traen un texto pensado para decir qué hacer.
  if ((status === 413 || status === 400) && message) return message;
  if (status === 400) return MENSAJES_RESUMEN.parametros;
  if (status >= 500) return MENSAJES_RESUMEN.servidor;
  return message ?? MENSAJES_RESUMEN.servidor;
}

/** Entrega el blob al navegador con un ancla temporal y revoca la URL pasado un rato (no antes: Safari la necesita viva). */
export function entregarBlob(blob: Blob, nombre: string, revocarEnMs = 30_000): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = nombre;
  a.rel = "noopener";
  a.style.display = "none";
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), revocarEnMs);
}

/**
 * Descarga el resumen mensual. Resuelve con el nombre del archivo entregado; si algo falla, RECHAZA con un
 * `ErrorDescargaResumen` cuyo `message` ya está en español para mostrarlo tal cual.
 */
export async function descargarResumenMensual(params: DescargaResumenParams): Promise<{ nombre: string }> {
  let res: Response;
  try {
    // GET sin cuerpo ni cabeceras propias: no hace falta CSRF ni preflight CORS. La cookie HttpOnly viaja con `credentials`.
    res = await fetch(`${API_BASE_URL}${rutaResumenMensual(params)}`, { credentials: "include" });
  } catch {
    throw new ErrorDescargaResumen(MENSAJES_RESUMEN.red, 0);
  }

  if (!res.ok) {
    const data = await res.json().catch(() => null);
    throw new ErrorDescargaResumen(mensajeParaError(res.status, data), res.status, leerError(data).code);
  }

  // Un 200 que no es el archivo (un proxy, una página de error) no se guarda como si lo fuera.
  const tipo = (res.headers.get("Content-Type") ?? "").split(";")[0].trim().toLowerCase();
  if (tipo !== MIME_RESUMEN[params.formato]) {
    throw new ErrorDescargaResumen(MENSAJES_RESUMEN.invalido, res.status);
  }

  // El cuerpo también puede cortarse a mitad de camino (se cae la conexión, el proxy cierra el stream): `blob()` rechaza
  // con un `TypeError` crudo. Se traduce igual que un fallo de `fetch`, para que la promesa de arriba («siempre
  // `ErrorDescargaResumen`») sea cierta y quien llama no tenga que adivinar qué es un error «de la librería».
  let blob: Blob;
  try {
    blob = await res.blob();
  } catch {
    throw new ErrorDescargaResumen(MENSAJES_RESUMEN.red, res.status);
  }
  const nombre = nombreDesdeContentDisposition(res.headers.get("Content-Disposition")) ?? nombreArchivoResumen(params);
  try {
    entregarBlob(blob, nombre);
  } catch {
    // `URL.createObjectURL` puede fallar (memoria, navegador restringido): el documento llegó, pero no se pudo guardar.
    throw new ErrorDescargaResumen(MENSAJES_RESUMEN.archivo, res.status);
  }
  return { nombre };
}

export const resumenMensualApi = {
  descargar: descargarResumenMensual,
  ruta: rutaResumenMensual,
};
